<script setup>
import { computed, reactive, ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import types from 'dashboard/store/mutation-types';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import WootButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';

// Components
import CampaignMetricsSection from '../components/CampaignMetricsSection.vue';
import CampaignFilters from '../components/CampaignFilters.vue';
import CampaignDetailsTable from '../components/CampaignDetailsTable.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const campaignDetails = useMapGetter('campaignDetails/getRecords');
const campaignDetailsMeta = useMapGetter('campaignDetails/getMeta');
const campaignDetailsUI = useMapGetter('campaignDetails/getUIFlags');
const labelRecords = useMapGetter('labels/getLabels');

const selectedCampaign = ref(null);
const confirmDeleteCampaignDialogRef = ref(null);

const tagDialogRef = ref(null);
const selectedLabels = ref([]);
const isTagging = ref(false);
const isResending = ref(false);
const isExporting = ref(false);
const isPausing = ref(false);
const isResuming = ref(false);

const selectedDetailIds = ref(new Set());
const currentPage = ref(1);

const detailsMeta = computed(() => ({
  total: campaignDetailsMeta.value?.total || 0,
  delivered: campaignDetailsMeta.value?.delivered || 0,
  read: campaignDetailsMeta.value?.read || 0,
  replied: campaignDetailsMeta.value?.replied || 0,
  page: campaignDetailsMeta.value?.page || 1,
  per_page: campaignDetailsMeta.value?.per_page || 25,
  error_codes: campaignDetailsMeta.value?.error_codes || [],
}));

const isSelectAllMode = ref(false);

const perPage = computed(() => detailsMeta.value.per_page || 25);
const campaignDisplayId = computed(() => Number(route.params.campaignId));

const filterState = reactive({
  q: '',
  delivery_status: '',
  read_status: '',
  reply_status: '',
  delivered_from: '',
  delivered_to: '',
  read_from: '',
  read_to: '',
  error_code: '',
});

const allCampaigns = computed(
  () => getters['campaigns/getAllCampaigns']?.value || []
);

const campaignRecord = computed(() => {
  return (
    allCampaigns.value.find(
      campaign =>
        (campaign.display_id || campaign.id) === campaignDisplayId.value
    ) || null
  );
});

const isCampaignCompleted = computed(() => {
  const status = campaignRecord.value?.campaign_status;
  return status === 'completed' || status === 1;
});

const labelOptions = computed(() => {
  if (!labelRecords.value) return [];
  return labelRecords.value.map(label => ({
    value: label.title,
    label: label.title,
  }));
});

const selectedCount = computed(() => selectedDetailIds.value.size);

const selectedOnPageCount = computed(() => {
  const idsOnPage = new Set(campaignDetails.value.map(detail => detail.id));
  let total = 0;
  selectedDetailIds.value.forEach(id => {
    if (idsOnPage.has(id)) {
      total += 1;
    }
  });
  return total;
});

const isIndeterminate = computed(() => {
  if (!campaignDetails.value.length) return false;
  return (
    selectedOnPageCount.value > 0 &&
    selectedOnPageCount.value < campaignDetails.value.length
  );
});

const isAllSelected = computed(() => {
  if (!campaignDetails.value.length) return false;
  return selectedOnPageCount.value === campaignDetails.value.length;
});

const buildParams = () => {
  const params = {
    ...filterState,
    page: currentPage.value,
    per_page: perPage.value,
  };
  Object.keys(params).forEach(key => {
    if (
      params[key] === '' ||
      params[key] === null ||
      params[key] === undefined
    ) {
      delete params[key];
    }
  });
  return params;
};

const fetchCampaignDetails = async () => {
  if (!campaignDisplayId.value) return;

  try {
    await store.dispatch('campaignDetails/fetch', {
      campaignId: campaignDisplayId.value,
      params: buildParams(),
    });
    pruneSelection();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CAMPAIGN.DETAILS.ALERTS.FETCH_ERROR')
    );
  }
};

const pruneSelection = () => {
  if (isSelectAllMode.value) return;
  const currentIds = new Set(campaignDetails.value.map(detail => detail.id));
  selectedDetailIds.value = new Set(
    [...selectedDetailIds.value].filter(id => currentIds.has(id))
  );
};

const selectAllAcrossPages = () => {
  isSelectAllMode.value = true;
};

const clearSelection = () => {
  selectedDetailIds.value = new Set();
  isSelectAllMode.value = false;
};

const debouncedFetch = useDebounceFn(() => {
  currentPage.value = 1;
  fetchCampaignDetails();
}, 300);

watch(
  () => ({ ...filterState }),
  () => {
    debouncedFetch();
  },
  { deep: true }
);

watch(
  () => campaignDisplayId.value,
  () => {
    selectedDetailIds.value = new Set();
    currentPage.value = 1;
    fetchCampaignDetails();
  }
);

const handlePageChange = value => {
  currentPage.value = value;
  fetchCampaignDetails();
};

const toggleSelectAll = () => {
  if (isAllSelected.value) {
    campaignDetails.value.forEach(detail =>
      selectedDetailIds.value.delete(detail.id)
    );
  } else {
    campaignDetails.value.forEach(detail =>
      selectedDetailIds.value.add(detail.id)
    );
  }
};

const hasSelection = computed(
  () => selectedDetailIds.value.size > 0 || isSelectAllMode.value
);

const toggleSelection = id => {
  if (selectedDetailIds.value.has(id)) {
    selectedDetailIds.value.delete(id);
  } else {
    selectedDetailIds.value.add(id);
  }
  selectedDetailIds.value = new Set(selectedDetailIds.value);
};

const openTagDialog = () => {
  if (!hasSelection.value) return;
  selectedLabels.value = [];
  tagDialogRef.value?.open();
};

const applyTagging = async () => {
  if (!selectedLabels.value.length || !hasSelection.value) return;
  isTagging.value = true;
  try {
    await store.dispatch('campaignDetails/bulkTag', {
      campaignId: campaignDisplayId.value,
      payload: {
        detail_ids: isSelectAllMode.value ? [] : [...selectedDetailIds.value],
        labels: selectedLabels.value,
        select_all: isSelectAllMode.value,
        ...filterState,
      },
    });
    useAlert(
      t('CAMPAIGN.DETAILS.ALERTS.TAG_SUCCESS', {
        count: selectedCount.value,
      })
    );
    tagDialogRef.value?.close();
    clearSelection();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CAMPAIGN.DETAILS.ALERTS.TAG_ERROR')
    );
  } finally {
    isTagging.value = false;
  }
};

const resendSelected = async () => {
  if (!hasSelection.value) return;
  isResending.value = true;
  try {
    await store.dispatch('campaignDetails/bulkResend', {
      campaignId: campaignDisplayId.value,
      payload: {
        detail_ids: isSelectAllMode.value ? [] : [...selectedDetailIds.value],
        select_all: isSelectAllMode.value,
        ...filterState,
      },
    });
    store.commit(`campaignDetails/${types.RESET_CAMPAIGN_DETAILS_STATS}`, [
      ...selectedDetailIds.value,
    ]);
    useAlert(
      t('CAMPAIGN.DETAILS.ALERTS.RESEND_SUCCESS', {
        count: selectedCount.value,
      })
    );
    clearSelection();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CAMPAIGN.DETAILS.ALERTS.RESEND_ERROR')
    );
  } finally {
    isResending.value = false;
  }
};

const exportDetails = async () => {
  isExporting.value = true;
  try {
    const params = buildParams();
    // Exclude pagination for export
    delete params.page;
    delete params.per_page;

    if (hasSelection.value && !isSelectAllMode.value) {
      params.detail_ids = [...selectedDetailIds.value];
    }
    const response = await store.dispatch('campaignDetails/export', {
      campaignId: campaignDisplayId.value,
      params,
    });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `campaign-${campaignDisplayId.value}-details.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    useAlert(t('CAMPAIGN.DETAILS.ALERTS.EXPORT_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CAMPAIGN.DETAILS.ALERTS.EXPORT_ERROR')
    );
  } finally {
    isExporting.value = false;
  }
};

const resetFilters = () => {
  filterState.q = '';
  filterState.delivery_status = '';
  filterState.read_status = '';
  filterState.reply_status = '';
  filterState.delivered_from = '';
  filterState.delivered_to = '';
  filterState.read_from = '';
  filterState.read_to = '';
  filterState.error_code = '';
};

const applyQuickFilter = type => {
  if (type === 'undelivered') {
    filterState.delivery_status = 'failed';
  }
  if (type === 'unread') {
    filterState.read_status = 'unread';
  }
};

const pauseCampaign = async () => {
  if (!campaignRecord.value) return;
  isPausing.value = true;
  try {
    await store.dispatch('campaigns/pause', campaignRecord.value.id);
    useAlert(t('CAMPAIGN.DETAILS.ALERTS.PAUSE_SUCCESS'));
    await store.dispatch('campaigns/get');
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('CAMPAIGN.DETAILS.ALERTS.PAUSE_ERROR')
    );
  } finally {
    isPausing.value = false;
  }
};

const resumeCampaign = async () => {
  if (!campaignRecord.value) return;
  isResuming.value = true;
  try {
    await store.dispatch('campaigns/resume', campaignRecord.value.id);
    useAlert(t('CAMPAIGN.DETAILS.ALERTS.RESUME_SUCCESS'));
    await store.dispatch('campaigns/get');
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('CAMPAIGN.DETAILS.ALERTS.RESUME_ERROR')
    );
  } finally {
    isResuming.value = false;
  }
};

const openDeleteDialog = () => {
  if (!campaignRecord.value) return;

  selectedCampaign.value = campaignRecord.value;
  confirmDeleteCampaignDialogRef.value?.dialogRef.open();
};

const navigateBack = () => {
  const campaign = campaignRecord.value;

  // Default to the same campaign type we came from
  let routeName = 'campaigns_whatsapp_index'; // Default to WhatsApp

  if (campaign?.inbox?.channel_type === 'Channel::Whatsapp') {
    routeName = 'campaigns_whatsapp_index';
  } else if (campaign?.inbox?.channel_type === 'Channel::Sms') {
    routeName = 'campaigns_sms_index';
  } else if (campaign?.inbox?.channel_type === 'Channel::TwilioSms') {
    routeName = 'campaigns_sms_index';
  } else if (campaign?.message_type === 'live_chat') {
    routeName = 'campaigns_livechat_index';
  }

  router.push({
    name: routeName,
    params: route.params,
  });
};

const navigateToConversation = conversationId => {
  const campaign = campaignRecord.value;
  if (!campaign || !campaign.inbox) return;

  router.push({
    name: 'conversation_through_inbox',
    params: {
      accountId: route.params.accountId,
      inbox_id: campaign.inbox.id,
      conversation_id: conversationId,
    },
  });
};

const onCampaignDetailUpdated = data => {
  if (Number(data.campaign_id) === campaignDisplayId.value) {
    store.dispatch('campaignDetails/updateRecord', data);
  }
};

onMounted(async () => {
  if (!allCampaigns.value.length) {
    await store.dispatch('campaigns/get');
  }
  store.dispatch('labels/get');
  fetchCampaignDetails();

  emitter.on(BUS_EVENTS.CAMPAIGN_DETAIL_UPDATED, onCampaignDetailUpdated);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.CAMPAIGN_DETAIL_UPDATED, onCampaignDetailUpdated);
});
</script>

<template>
  <section class="px-6 py-6 mx-auto w-full max-w-[75rem]">
    <!-- Header -->
    <div class="flex items-center justify-between gap-4 mb-6">
      <div class="flex flex-col gap-1">
        <button
          class="flex items-center gap-2 text-sm text-n-slate-11 hover:text-n-slate-12"
          @click="navigateBack"
        >
          <span class="i-lucide-arrow-left size-4" />
          {{ t('CAMPAIGN.DETAILS.BACK') }}
        </button>
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ campaignRecord?.title || t('CAMPAIGN.DETAILS.HEADER') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.DETAILS.SUBTITLE') }}
        </p>
      </div>
      <div
        v-if="campaignRecord && !isCampaignCompleted"
        class="flex flex-wrap items-center gap-2"
      >
        <WootButton
          v-if="campaignRecord?.enabled"
          size="sm"
          variant="ghost"
          icon="i-lucide-pause"
          :label="t('CAMPAIGN.DETAILS.PAUSE')"
          :is-loading="isPausing"
          @click="pauseCampaign"
        />
        <WootButton
          v-else
          size="sm"
          variant="ghost"
          icon="i-lucide-play"
          :label="t('CAMPAIGN.DETAILS.RESUME')"
          :is-loading="isResuming"
          @click="resumeCampaign"
        />
        <WootButton
          size="sm"
          color="ruby"
          variant="outline"
          :label="t('CAMPAIGN.DETAILS.DELETE')"
          @click="openDeleteDialog"
        />
      </div>
      <div v-else class="flex flex-wrap items-center gap-2">
        <WootButton
          size="sm"
          color="ruby"
          variant="outline"
          :label="t('CAMPAIGN.DETAILS.DELETE')"
          @click="openDeleteDialog"
        />
      </div>
    </div>

    <!-- Metrics -->
    <CampaignMetricsSection :meta="detailsMeta" />

    <!-- Filters -->
    <CampaignFilters
      v-model="filterState"
      :error-codes="detailsMeta.error_codes"
      @reset="resetFilters"
    />

    <!-- Selection Bar -->
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div class="flex items-center gap-2 text-sm text-n-slate-11">
        <Checkbox
          :model-value="isAllSelected"
          :indeterminate="isIndeterminate"
          @change="toggleSelectAll"
        />
        <span>
          {{
            t('CAMPAIGN.DETAILS.SELECTION.MESSAGE', { count: selectedCount })
          }}
        </span>
        <span
          v-if="
            !isSelectAllMode &&
            selectedCount === campaignDetails.length &&
            detailsMeta.total > campaignDetails.length
          "
          class="ml-2 text-sm text-n-slate-11"
        >
          Running on page selection.
          <button
            class="text-n-blue-11 hover:underline"
            @click="selectAllAcrossPages"
          >
            Select all {{ detailsMeta.total }} items across all pages?
          </button>
        </span>
        <span v-if="isSelectAllMode" class="ml-2 text-sm text-n-slate-11">
          All {{ detailsMeta.total }} items selected.
        </span>
        <WootButton
          v-if="hasSelection"
          variant="link"
          size="xs"
          :label="t('CAMPAIGN.DETAILS.SELECTION.CLEAR')"
          @click="clearSelection"
        />
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <WootButton
          variant="ghost"
          size="sm"
          :label="t('CAMPAIGN.DETAILS.QUICK.UND_DELIVERED')"
          @click="applyQuickFilter('undelivered')"
        />
        <WootButton
          variant="ghost"
          size="sm"
          :label="t('CAMPAIGN.DETAILS.QUICK.UNREAD')"
          @click="applyQuickFilter('unread')"
        />
        <WootButton
          size="sm"
          variant="outline"
          icon="i-lucide-tag"
          :disabled="!hasSelection"
          :label="t('CAMPAIGN.DETAILS.ACTIONS.TAG')"
          @click="openTagDialog"
        />
        <WootButton
          size="sm"
          variant="outline"
          icon="i-lucide-refresh-ccw"
          :disabled="!hasSelection || isResending"
          :is-loading="isResending"
          :label="t('CAMPAIGN.DETAILS.ACTIONS.RESEND')"
          @click="resendSelected"
        />
        <WootButton
          size="sm"
          variant="solid"
          icon="i-lucide-download"
          :is-loading="isExporting"
          :label="t('CAMPAIGN.DETAILS.ACTIONS.EXPORT')"
          @click="exportDetails"
        />
      </div>
    </div>

    <!-- Table -->
    <CampaignDetailsTable
      :campaign-details="campaignDetails"
      :details-meta="detailsMeta"
      :is-loading="campaignDetailsUI.isFetching"
      :selected-detail-ids="selectedDetailIds"
      :is-all-selected="isAllSelected"
      :current-page="currentPage"
      @toggle-select-all="toggleSelectAll"
      @toggle-selection="toggleSelection"
      @view-conversation="navigateToConversation"
      @page-change="handlePageChange"
    />

    <!-- Dialogs -->
    <Dialog
      ref="tagDialogRef"
      width="md"
      :title="t('CAMPAIGN.DETAILS.TAG_DIALOG.TITLE')"
      :description="
        t('CAMPAIGN.DETAILS.TAG_DIALOG.DESCRIPTION', { count: selectedCount })
      "
      :disable-confirm-button="!selectedLabels.length"
      :is-loading="isTagging"
      @confirm="applyTagging"
    >
      <TagMultiSelectComboBox
        v-model="selectedLabels"
        :options="labelOptions"
        :placeholder="t('CAMPAIGN.DETAILS.TAG_DIALOG.PLACEHOLDER')"
        :empty-state="t('CAMPAIGN.DETAILS.TAG_DIALOG.EMPTY')"
      />
    </Dialog>

    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
      @delete="navigateBack"
    />
  </section>
</template>
